/**
 * Custom KLineCharts indicators that render market breadth data
 * from the screener_counts D1 table (loaded into breadthStore).
 *
 * Each indicator uses calc() to return real data so that KLineCharts can
 * auto-scale the yAxis and drive repaints on scroll/zoom. Three indicators:
 *
 * MA_BREADTH  — stacked area: # stocks above SMA10, EMA21, EMA50, EMA200
 * WEEK52_HLN  — diverging bars (highs ↑ green / lows ↓ red) + net line overlay
 */

import {
  IndicatorSeries,
  PolygonType,
  LineType,
  type IndicatorTemplate,
  type IndicatorDrawParams,
} from 'klinecharts';
import { useBreadthStore } from '@/store/breadthStore';

// ── helpers ───────────────────────────────────────────────────────────────────

type KBarBasic = { timestamp: number };

function rowAt(tsMs: number) {
  const { data, tsIndex } = useBreadthStore.getState();
  const idx = tsIndex.get(tsMs);
  return idx !== undefined ? data[idx] : undefined;
}

// ── MA_BREADTH ────────────────────────────────────────────────────────────────
// Stacked area: 4 filled bands (SMA10, EMA21, EMA50, EMA200) drawn back-to-front
// from baseline 0 to each MA's count value.  The visible "band" for each MA is
// the gap between its fill edge and the next-smaller MA's fill edge — the width
// of each band is the story.  Early recovery: EMA200 band wide, SMA10/EMA21 thin.

const MA_COLORS = ['#FF9600', '#9D65C9', '#2196F3', '#E11D74'];
const MA_KEYS   = ['count_above_sma10', 'count_above_ema21', 'count_above_ema50', 'count_above_ema200'] as const;
const MA_LABELS = ['SMA10', 'EMA21', 'EMA50', 'EMA200'];
// Result property names in the same index order as MA_COLORS/MA_KEYS/MA_LABELS
const MA_RESULT_KEYS = ['sma10', 'ema21', 'ema50', 'ema200'] as const;
// Draw back-to-front: EMA200 (index 3) first as background, SMA10 (index 0) last on top
const MA_DRAW_ORDER = [3, 2, 1, 0] as const;

type MABreadthResult = {
  sma10:  number | null;
  ema21:  number | null;
  ema50:  number | null;
  ema200: number | null;
  _total: number | null;
};

const maBreadth: IndicatorTemplate<MABreadthResult> = {
  name: 'MA_BREADTH',
  shortName: 'MA Breadth',
  series: IndicatorSeries.Normal,
  shouldOhlc: false,

  // figures drive yAxis auto-scaling via calc() values.
  // draw() returns true to suppress KLineCharts' default line rendering.
  figures: [
    { key: 'sma10',  title: 'SMA10: ',  type: 'line' },
    { key: 'ema21',  title: 'EMA21: ',  type: 'line' },
    { key: 'ema50',  title: 'EMA50: ',  type: 'line' },
    { key: 'ema200', title: 'EMA200: ', type: 'line' },
  ],

  styles: {
    lines: MA_COLORS.map((color) => ({
      style:       LineType.Solid,
      size:        0,   // hidden — draw() handles all visuals
      color,
      smooth:      false,
      dashedValue: [2, 2],
    })),
  },

  calc: (dataList) =>
    (dataList as unknown as KBarBasic[]).map((bar) => {
      const row = rowAt(bar.timestamp);
      if (!row) return { sma10: null, ema21: null, ema50: null, ema200: null, _total: null };
      return {
        sma10:  row.count_above_sma10  ?? null,
        ema21:  row.count_above_ema21  ?? null,
        ema50:  row.count_above_ema50  ?? null,
        ema200: row.count_above_ema200 ?? null,
        _total: row.total_stocks       ?? null,
      };
    }),

  draw: (params: IndicatorDrawParams<MABreadthResult>) => {
    const { ctx, visibleRange, indicator, xAxis, yAxis } = params;
    const results = indicator.result;
    const y0 = yAxis.convertToPixel(0);
    const { from, to } = visibleRange;
    if (to <= from) return true;

    // Collect pixel { x, y } per series across all visible bars
    type Pt = { x: number; y: number };
    const seriesPts: Pt[][] = [[], [], [], []]; // index = MA_RESULT_KEYS index

    for (let i = from; i <= to; i++) {
      const r = results[i];
      if (!r) continue;
      const x = xAxis.convertToPixel(i);
      MA_RESULT_KEYS.forEach((key, si) => {
        const val = r[key];
        if (val != null) seriesPts[si].push({ x, y: yAxis.convertToPixel(val) });
      });
    }

    ctx.save();
    ctx.lineJoin = 'round';

    // ── filled areas: EMA200 (back) → EMA21 → EMA50 → SMA10 (front) ─────────
    // Each fills from baseline y0 upward to its own value.  Because we draw
    // back-to-front the visible coloured band per MA is the gap between its
    // fill top and the next-smaller MA's fill top — exactly "stacked area".
    for (const si of MA_DRAW_ORDER) {
      const pts = seriesPts[si];
      if (pts.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(pts[0].x, y0);
      ctx.lineTo(pts[0].x, pts[0].y);
      for (let pi = 1; pi < pts.length; pi++) ctx.lineTo(pts[pi].x, pts[pi].y);
      ctx.lineTo(pts[pts.length - 1].x, y0);
      ctx.closePath();
      ctx.fillStyle = MA_COLORS[si] + '44'; // ~27% opacity
      ctx.fill();
    }

    // ── stroke lines on top of fills (SMA10 drawn last = on top) ─────────────
    for (let si = 0; si < 4; si++) {
      const pts = seriesPts[si];
      if (pts.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let pi = 1; pi < pts.length; pi++) ctx.lineTo(pts[pi].x, pts[pi].y);
      ctx.strokeStyle = MA_COLORS[si];
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    ctx.restore();
    return true; // suppress default figure rendering
  },

  createTooltipDataSource: (params) => {
    const p = params as unknown as { kLineDataList: KBarBasic[]; crosshair: { dataIndex?: number } };
    const bar = p.kLineDataList[p.crosshair.dataIndex ?? 0];
    const row = bar ? rowAt(bar.timestamp) : undefined;
    return {
      name: 'MA Breadth',
      calcParamsText: '',
      icons: [],
      values: row
        ? MA_KEYS.map((k, i) => {
            const val   = row[k] ?? null;
            const total = row.total_stocks ?? null;
            const pct   = val != null && total ? ((val / total) * 100).toFixed(1) : null;
            return {
              title: MA_LABELS[i],
              value: val != null ? (pct != null ? `${val} (${pct}%)` : String(val)) : '—',
            };
          })
        : [],
    };
  },
};

// ── WEEK52_HLN ───────────────────────────────────────────────────────────────
// Combined indicator: diverging bars (highs ↑ teal / lows ↓ red, the zero axis is
// the decision line) with the net-new-highs line overlaid in the same pane.
// Rendering is fully custom via draw() so bars and line coexist cleanly.

const W52_COLOR_HIGH = '#26A69A';
const W52_COLOR_LOW  = '#EF5350';
const W52_COLOR_NET  = '#2196F3';

type W52HLNResult = {
  highs:    number | null;
  lows_neg: number | null; // stored as negative so yAxis auto-scales symmetrically
  net:      number | null;
  _total:   number | null;
};

const week52HLN: IndicatorTemplate<W52HLNResult> = {
  name: 'WEEK52_HLN',
  shortName: '52W H/L+Net',
  series: IndicatorSeries.Normal,
  shouldOhlc: false,

  // figures drive yAxis auto-scaling; draw() returns true to suppress default rendering.
  figures: [
    { key: 'highs',    title: 'Highs: ', type: 'bar'  },
    { key: 'lows_neg', title: 'Lows: ',  type: 'bar'  },
    { key: 'net',      title: 'Net: ',   type: 'line' },
  ],

  styles: {
    bars: [
      { style: PolygonType.Fill, upColor: W52_COLOR_HIGH, downColor: W52_COLOR_HIGH, noChangeColor: W52_COLOR_HIGH, borderSize: 0, borderStyle: LineType.Solid, borderDashedValue: [] },
      { style: PolygonType.Fill, upColor: W52_COLOR_LOW,  downColor: W52_COLOR_LOW,  noChangeColor: W52_COLOR_LOW,  borderSize: 0, borderStyle: LineType.Solid, borderDashedValue: [] },
    ],
    lines: [{ style: LineType.Solid, size: 0, color: W52_COLOR_NET, smooth: false, dashedValue: [2, 2] }],
  },

  calc: (dataList) =>
    (dataList as unknown as KBarBasic[]).map((bar) => {
      const row = rowAt(bar.timestamp);
      if (!row) return { highs: null, lows_neg: null, net: null, _total: null };
      const h = row.count_52w_high ?? null;
      const l = row.count_52w_low  ?? null;
      return {
        highs:    h,
        lows_neg: l != null ? -l : null,
        net:      h != null && l != null ? h - l : null,
        _total:   row.total_stocks ?? null,
      };
    }),

  draw: (params: IndicatorDrawParams<W52HLNResult>) => {
    const { ctx, visibleRange, indicator, xAxis, yAxis, barSpace } = params;
    const results = indicator.result;
    const { from, to } = visibleRange;
    if (to <= from) return true;

    const y0       = yAxis.convertToPixel(0);
    const barW     = Math.max(1, Math.round(barSpace.bar * 0.6));
    const halfBar  = Math.floor(barW / 2);

    ctx.save();

    // ── diverging bars ────────────────────────────────────────────────────────
    for (let i = from; i <= to; i++) {
      const r = results[i];
      if (!r) continue;
      const cx = xAxis.convertToPixel(i);
      const x  = cx - halfBar;

      if (r.highs != null && r.highs !== 0) {
        const yTop = yAxis.convertToPixel(r.highs);
        ctx.fillStyle = W52_COLOR_HIGH + 'cc'; // ~80% opacity
        ctx.fillRect(x, yTop, barW, y0 - yTop);
      }
      if (r.lows_neg != null && r.lows_neg !== 0) {
        const yBot = yAxis.convertToPixel(r.lows_neg);
        ctx.fillStyle = W52_COLOR_LOW + 'cc';
        ctx.fillRect(x, y0, barW, yBot - y0);
      }
    }

    // ── zero axis guide line ──────────────────────────────────────────────────
    const leftX  = xAxis.convertToPixel(from);
    const rightX = xAxis.convertToPixel(to);
    ctx.beginPath();
    ctx.moveTo(leftX,  y0);
    ctx.lineTo(rightX, y0);
    ctx.strokeStyle = 'rgba(255,255,255,0.20)';
    ctx.lineWidth   = 1;
    ctx.setLineDash([4, 4]);
    ctx.stroke();
    ctx.setLineDash([]);

    // ── net line overlay ──────────────────────────────────────────────────────
    ctx.beginPath();
    let firstNet = true;
    for (let i = from; i <= to; i++) {
      const r = results[i];
      if (!r || r.net == null) { firstNet = true; continue; }
      const x = xAxis.convertToPixel(i);
      const y = yAxis.convertToPixel(r.net);
      if (firstNet) { ctx.moveTo(x, y); firstNet = false; }
      else           { ctx.lineTo(x, y); }
    }
    ctx.strokeStyle = W52_COLOR_NET;
    ctx.lineWidth   = 1.5;
    ctx.stroke();

    ctx.restore();
    return true;
  },

  createTooltipDataSource: (params) => {
    const p = params as unknown as { kLineDataList: KBarBasic[]; crosshair: { dataIndex?: number } };
    const bar = p.kLineDataList[p.crosshair.dataIndex ?? 0];
    const row = bar ? rowAt(bar.timestamp) : undefined;
    if (!row) return { name: '52W H/L + Net', calcParamsText: '', icons: [], values: [] };
    const h   = row.count_52w_high ?? null;
    const l   = row.count_52w_low  ?? null;
    const net = h != null && l != null ? h - l : null;
    const tot = row.total_stocks ?? null;
    function fmtPct(v: number) { return tot ? ` (${(v / tot * 100).toFixed(1)}%)` : ''; }
    return {
      name: '52W H/L + Net',
      calcParamsText: '',
      icons: [],
      values: [
        { title: 'New Highs', value: h   != null ? `${h}${fmtPct(h)}`   : '—' },
        { title: 'New Lows',  value: l   != null ? `${l}${fmtPct(l)}`   : '—' },
        { title: 'Net',       value: net != null ? (net >= 0 ? '+' : '') + net : '—' },
      ],
    };
  },
};

export const breadthIndicators = [
  maBreadth   as IndicatorTemplate<unknown>,
  week52HLN   as IndicatorTemplate<unknown>,
];
export { maBreadth, week52HLN };

